/**
 * Combat impact-feedback dispatcher (ADR-0017 §2–§4). `playJuice(event, side)`
 * maps a resolution `CombatEvent` to a hand-rolled visual moment so hits, blocks
 * and status ticks land physically. One dispatcher serves both callers: the live
 * player resolution and the HEX-69 paced enemy-turn replay (CardCombatOverlay
 * flushes newly-revealed events through it).
 *
 * Moments (ADR-0017 §2):
 *   J1 hit flash + shake  — combatant takes HP damage          → `--hit` on the card
 *   J2 damage/heal/block number (pooled, colour-coded)         → floater
 *   J3 block-break shatter (4 static-angle shards) / soft cue  → floaters
 *   J4 status apply / tick cue                                 → pill pulse (+ J2)
 *   J5 death collapse                                          — NOT here; the
 *      overlay renders it declaratively (a `--dead` class survives re-render).
 *
 * Everything is transform/opacity/filter only (GPU-compositable, no layout
 * thrash). The floater pool caps concurrent nodes and recycles the oldest on
 * overflow (ADR-0017 §4). `reducedMotion` collapses motion to static numbers and
 * drops the shake/shatter (the CSS `prefers-reduced-motion` block backs this up).
 * Unrecognised event kinds are a graceful no-op.
 */

import type { CombatEvent, Side } from '../cardEngine';

/** Concurrent floater cap (ADR-0017 §4). Excess spawns recycle the oldest. */
export const FLOATER_CAP = 8;

const HIT_CLASS = 'hex6-card-combat__combatant--hit';
const STATUS_CUE_CLASS = 'hex6-card-combat__status--cue';
const FLOATER_CLASS = 'hex6-card-combat__floater';

// Colour codes (ADR-0017 §2 J2).
const COLOUR_DAMAGE = '#e2603a';
const COLOUR_BLOCK = '#9aa0b4';
const COLOUR_POISON = '#8fbf5f';
// HEX-88 — fizzle: a recursive roll that matched nothing. Muted grey to read as
// a non-event, distinct from the live damage/block/poison numbers.
const COLOUR_FIZZLE = '#6b7080';

// Lifetimes — mirror the CSS keyframe durations so the pool reclaims nodes even
// where `animationend` never fires (reduced motion, detached node, tests).
const NUMBER_MS = 800;
const NUMBER_STATIC_MS = 400;
const FLASH_MS = 160;
const SHATTER_MS = 360;
const STATUS_MS = 220;

/** Static shard vectors for the block-break burst (no RNG — ADR-0017 §2 J3). */
const SHARDS: ReadonlyArray<{ x: number; y: number; r: number }> = [
  { x: -22, y: -16, r: -42 },
  { x: 20, y: -18, r: 38 },
  { x: -18, y: 16, r: 52 },
  { x: 22, y: 14, r: -56 },
];

export interface JuiceDeps {
  /** Stable overlay root; the juice layer mounts here, above the bands. */
  root: HTMLElement;
  /** Resolve the currently-rendered combatant card for a side (or null). */
  cardOf: (side: Side) => HTMLElement | null;
  /** OS reduce-motion preference — collapses motion to static feedback. */
  reducedMotion: boolean;
}

export class CombatJuice {
  private readonly layer: HTMLDivElement;
  private readonly free: HTMLSpanElement[] = [];
  /** Live floaters, oldest first — `shift()` recycles the oldest on overflow. */
  private readonly active: HTMLSpanElement[] = [];
  private readonly timers = new Set<number>();
  private gen = 0;

  constructor(private readonly deps: JuiceDeps) {
    this.layer = document.createElement('div');
    this.layer.className = 'hex6-card-combat__juice';
    this.layer.dataset.role = 'juice-layer';
    deps.root.appendChild(this.layer);
  }

  /** Map a resolution event to its moment. `side` is the event's subject (whose
   * card the feedback plays on). Unknown kinds are a no-op (J5 death is rendered
   * declaratively by the overlay, so `combatEnded` is intentionally ignored). */
  playJuice(event: CombatEvent, side: Side): void {
    switch (event.kind) {
      case 'damage': {
        const hpLost = event.amount - event.blocked;
        if (hpLost > 0) {
          this.flash(side); // J1
          this.number(side, `-${hpLost}`, COLOUR_DAMAGE); // J2
        }
        if (event.blocked > 0 && event.amount > event.blocked) {
          this.shatter(side); // J3 — block fully consumed and damage spilled over
        } else if (event.blocked > 0) {
          this.number(side, String(event.blocked), COLOUR_BLOCK); // J3 soft absorb cue
        }
        break;
      }
      case 'poisonTick': {
        if (event.amount > 0) {
          this.statusCue(side, 'poison'); // J4
          this.number(side, `-${event.amount}`, COLOUR_POISON); // J2 tinted
        }
        break;
      }
      case 'statusApplied': {
        this.statusCue(side, event.status.status); // J4
        break;
      }
      case 'rollFresh': {
        // HEX-88 — a fizzle (no matching branch) is otherwise silent. Surface a
        // subtle muted-grey "fizzle" floater on the acting side so the recursive
        // doesn't FEEL broken. Reuses the floater pool, so reduced motion gets
        // the static-fade fallback for free.
        if (event.fizzled) this.fizzle(side);
        break;
      }
      default:
        return; // graceful no-op
    }
  }

  destroy(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.free.length = 0;
    this.active.length = 0;
    this.layer.remove();
  }

  // ── moments ───────────────────────────────────────────────────────────────

  /** J1 — hit flash + shake on the struck card. Skipped under reduced motion. */
  private flash(side: Side): void {
    if (this.deps.reducedMotion) return;
    const card = this.deps.cardOf(side);
    if (!card) return;
    card.classList.remove(HIT_CLASS);
    void card.offsetWidth; // restart the keyframe if the class lingers
    card.classList.add(HIT_CLASS);
    this.after(FLASH_MS + 40, () => card.classList.remove(HIT_CLASS));
  }

  /** J4 — pulse the subject's status pill for `status` (icon scale + accent). */
  private statusCue(side: Side, status: string): void {
    if (this.deps.reducedMotion) return;
    const pill = this.deps
      .cardOf(side)
      ?.querySelector<HTMLElement>(`.hex6-card-combat__status[data-status="${status}"]`);
    if (!pill) return;
    pill.classList.remove(STATUS_CUE_CLASS);
    void pill.offsetWidth;
    pill.classList.add(STATUS_CUE_CLASS);
    this.after(STATUS_MS + 40, () => pill.classList.remove(STATUS_CUE_CLASS));
  }

  /** J2 — a pooled, colour-coded number popping at the combatant's info column. */
  private number(side: Side, text: string, colour: string): void {
    this.spawn((node) => {
      node.classList.add('hex6-card-combat__floater--number');
      node.textContent = text;
      node.style.color = colour;
      this.place(node, this.anchor(side, 'cinfo'));
      if (this.deps.reducedMotion) {
        node.classList.add('hex6-card-combat__floater--static');
        return NUMBER_STATIC_MS;
      }
      return NUMBER_MS;
    });
  }

  /** HEX-88 — fizzle cue: a muted "fizzle" floater at the acting side's info
   * column. A non-event by design, so no flash/shake — just the faint number-
   * style floater (it inherits the reduced-motion static-fade fallback). */
  private fizzle(side: Side): void {
    this.spawn((node) => {
      node.classList.add('hex6-card-combat__floater--number');
      node.classList.add('hex6-card-combat__floater--fizzle');
      node.textContent = 'fizzle';
      node.style.color = COLOUR_FIZZLE;
      this.place(node, this.anchor(side, 'cinfo'));
      if (this.deps.reducedMotion) {
        node.classList.add('hex6-card-combat__floater--static');
        return NUMBER_STATIC_MS;
      }
      return NUMBER_MS;
    });
  }

  /** J3 — four shards bursting from the (broken) shield. Skipped under reduced. */
  private shatter(side: Side): void {
    if (this.deps.reducedMotion) return;
    for (const shard of SHARDS) {
      this.spawn((node) => {
        node.classList.add('hex6-card-combat__floater--shard');
        node.style.setProperty('--sx', `${shard.x}px`);
        node.style.setProperty('--sy', `${shard.y}px`);
        node.style.setProperty('--sr', `${shard.r}deg`);
        this.place(node, this.anchor(side, 'block'));
        return SHATTER_MS;
      });
    }
  }

  // ── floater pool ────────────────────────────────────────────────────────────

  /** Acquire a floater, configure it via `configure` (returns its lifetime ms),
   * then schedule its return to the pool. The pool caps live nodes at
   * `FLOATER_CAP`, recycling the oldest when full (ADR-0017 §4). */
  private spawn(configure: (node: HTMLSpanElement) => number): void {
    const node = this.acquire();
    const g = String(++this.gen);
    node.dataset.gen = g;
    const ms = configure(node);
    const onEnd = (): void => {
      node.removeEventListener('animationend', onEnd);
      if (node.dataset.gen === g) this.release(node);
    };
    node.addEventListener('animationend', onEnd);
    this.after(ms, () => {
      if (node.dataset.gen === g) this.release(node);
    });
  }

  private acquire(): HTMLSpanElement {
    let node = this.free.pop();
    if (!node) {
      node = this.active.length >= FLOATER_CAP ? this.active.shift() : undefined;
      if (!node) {
        node = document.createElement('span');
        this.layer.appendChild(node);
      }
    }
    node.className = FLOATER_CLASS;
    node.style.cssText = '';
    node.textContent = '';
    node.hidden = false;
    this.active.push(node);
    return node;
  }

  private release(node: HTMLSpanElement): void {
    const i = this.active.indexOf(node);
    if (i === -1) return; // already released (animationend + timeout race)
    this.active.splice(i, 1);
    node.hidden = true;
    node.className = FLOATER_CLASS;
    node.style.cssText = '';
    this.free.push(node);
  }

  // ── positioning ─────────────────────────────────────────────────────────────

  /** Resolve a sub-anchor element within the subject's card for placement. */
  private anchor(side: Side, kind: 'cinfo' | 'block'): HTMLElement | null {
    const card = this.deps.cardOf(side);
    if (!card) return null;
    if (kind === 'block') {
      return (
        card.querySelector<HTMLElement>('.hex6-card-combat__block:not([hidden])') ??
        card.querySelector<HTMLElement>('.hex6-card-combat__hpline') ??
        card.querySelector<HTMLElement>('.hex6-card-combat__cinfo') ??
        card
      );
    }
    return card.querySelector<HTMLElement>('.hex6-card-combat__cinfo') ?? card;
  }

  /** Position a floater at the anchor's centre, relative to the overlay root.
   * (Rects are zero in jsdom/happy-dom — harmless; the pool logic is what tests
   * exercise.) */
  private place(node: HTMLSpanElement, anchor: HTMLElement | null): void {
    if (!anchor) return;
    const ar = anchor.getBoundingClientRect();
    const rr = this.deps.root.getBoundingClientRect();
    node.style.left = `${ar.left - rr.left + ar.width / 2}px`;
    node.style.top = `${ar.top - rr.top + ar.height / 2}px`;
  }

  private after(ms: number, fn: () => void): void {
    const t = setTimeout(() => {
      this.timers.delete(t);
      fn();
    }, ms) as unknown as number;
    this.timers.add(t);
  }
}
